import { useEffect, type RefObject } from 'react';
import { bindPairedIframeScroll, bindPairedScroll } from '../utils/pairedScroll';

/** Keep the first two matches of `selector` inside `rootRef` scrolled together. */
export function usePairedScrollSync(
  rootRef: RefObject<HTMLElement | null>,
  selector: string,
  enabled: boolean,
): void {
  useEffect(() => {
    if (!enabled) return;
    const root = rootRef.current;
    if (!root) return;
    const nodes = Array.from(root.querySelectorAll<HTMLElement>(selector));
    if (nodes.length < 2 || !nodes[0] || !nodes[1]) return;
    return bindPairedScroll(nodes[0], nodes[1]);
  }, [rootRef, selector, enabled]);
}

/** Sync scroll between two same-origin iframes after they load. */
export function usePairedIframeScrollSync(
  leftRef: RefObject<HTMLIFrameElement | null>,
  rightRef: RefObject<HTMLIFrameElement | null>,
  enabled: boolean,
  bindKey = '',
): void {
  useEffect(() => {
    if (!enabled) return;
    const left = leftRef.current;
    const right = rightRef.current;
    if (!left || !right) return;

    let unbind: (() => void) | undefined;
    let timer: number | undefined;
    let tries = 0;
    const attach = () => {
      const next = bindPairedIframeScroll(left, right);
      if (!next) return false;
      unbind?.();
      unbind = next;
      return true;
    };
    attach();
    left.addEventListener('load', attach);
    right.addEventListener('load', attach);
    const retry = () => {
      attach();
      if (++tries < 16) timer = window.setTimeout(retry, 250);
    };
    timer = window.setTimeout(retry, 250);
    return () => {
      left.removeEventListener('load', attach);
      right.removeEventListener('load', attach);
      if (timer) window.clearTimeout(timer);
      unbind?.();
    };
  }, [leftRef, rightRef, enabled, bindKey]);
}
